// Agents — a frota e o Edit Agent (docs/screens/15-edit-agent.md).
//
// O agente em edição tem duas metades: identidade/modelo no LiteLLM
// (GET/PATCH /v1/agents/{id}) e execução — prompt, tools, skills, repos,
// colaboradores — no arquivo versionado em agentes/ no GitHub. "Save" faz as
// duas escritas: contents primeiro (tem sha, é idempotente), depois o PATCH.

import { repoAtual, gateway, github, litellm, SourceError, type Agent } from "../api";
import { empty, errorBox, esc, head, pill, skeleton, table, toast } from "../ui";

// ---------------------------------------------------------------------- CSS

const CSS = `
.agents-form{max-width:430px}
.agents-head-left{display:flex;align-items:center;gap:10px}
.agents-back{padding:4px 10px;text-decoration:none}
.agents-sec{margin:22px 0}
.agents-sec-head{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:8px}
.agents-sec-title{font-size:13px;font-weight:700;margin:0}
.agents-count{font-size:12px;opacity:.7}
.agents-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 14px}
.agents-row+.agents-row{border-top:1px solid rgba(128,128,128,.15)}
.agents-row-title{font-size:13px;display:flex;align-items:center;gap:8px}
.agents-row-sub{font-size:12px;opacity:.6;margin-top:2px}
.agents-toggle{position:relative;display:inline-block;width:36px;height:20px;flex:none}
.agents-toggle input{position:absolute;inset:0;opacity:0;margin:0;cursor:pointer}
.agents-toggle input:disabled{cursor:not-allowed}
.agents-knob{display:block;position:absolute;inset:0;border-radius:999px;background:rgba(128,128,128,.35);transition:background .15s}
.agents-knob::after{content:"";position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:50%;background:#999;transition:transform .15s,background .15s}
.agents-toggle input:checked+.agents-knob{background:var(--accent,#eac54f)}
.agents-toggle input:checked+.agents-knob::after{transform:translateX(16px);background:#1a1a14}
.agents-toggle input:disabled+.agents-knob{opacity:.4}
.agents-found-head{display:flex;align-items:center;gap:8px;cursor:pointer;padding:10px 14px;list-style:none}
.agents-found-head::-webkit-details-marker{display:none}
.agents-chevron{display:inline-block;transition:transform .15s}
details[open]>summary .agents-chevron{transform:rotate(90deg)}
.agents-found-side{margin-left:auto;font-size:12px;opacity:.6}
.agents-found-body{border-top:1px solid rgba(128,128,128,.15)}
.agents-found-body>.hint{display:block;padding:10px 14px 0}
.agents-pre{white-space:pre-wrap;font-size:12px;line-height:1.5;padding:12px 14px;margin:0}
.agents-inst{min-height:280px}
.agents-hint{padding:12px 14px;font-size:12px;margin-top:10px}
.agents-hint a{text-decoration:underline}
`;
if (!document.getElementById("css-agents")) {
  const st = document.createElement("style");
  st.id = "css-agents";
  st.textContent = CSS;
  document.head.append(st);
}

// ------------------------------------------------------------------- apoios

/** Mesmo padrão de carregamento das outras páginas: esqueleto, erro tratado. */
async function load(el: HTMLElement, header: string, body: () => Promise<string>): Promise<void> {
  el.innerHTML = header + skeleton();
  try {
    el.innerHTML = header + (await body());
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}


const deB64 = (s: string): string =>
  new TextDecoder().decode(Uint8Array.from(atob(s.replace(/\s/g, "")), (c) => c.charCodeAt(0)));

const paraB64 = (s: string): string => {
  let bin = "";
  for (const b of new TextEncoder().encode(s)) bin += String.fromCharCode(b);
  return btoa(bin);
};

/** Um arquivo do repo — o GET de contents devolve o corpo em base64 + sha. */
const arquivo = (repo: string, path: string): Promise<{ sha: string; texto: string }> =>
  gateway<{ sha: string; content?: string }>(`github/repos/${repo}/contents/${path}`).then((f) => ({
    sha: f.sha,
    texto: f.content ? deB64(f.content) : "",
  }));

/** Frontmatter mínimo do arquivo do agente (convenção de subagent do Claude
 *  Code: markdown com frontmatter + system prompt). Só `chave: valor`. */
function parseAgentFile(texto: string): { meta: Record<string, string>; body: string } {
  const m = texto.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
  if (!m) return { meta: {}, body: texto };
  const meta: Record<string, string> = {};
  for (const linha of m[1].split(/\r?\n/)) {
    const kv = linha.match(/^([\w-]+):\s*(.*)$/);
    if (kv) meta[kv[1]] = kv[2];
  }
  return { meta, body: texto.slice(m[0].length) };
}

/** Lista em flow style: `[a, b]` ou `a, b`. */
const listaDe = (v: string | undefined): string[] =>
  (v ?? "").replace(/^\[|\]$/g, "").split(",").map((s) => s.trim()).filter(Boolean);

// checagem: o parser precisa round-tripar o que o Save escreve
{
  const t = parseAgentFile("---\nmemory: true\ntools: [Bash, Read]\n---\ncorpo");
  console.assert(
    t.meta.memory === "true" && listaDe(t.meta.tools).join(",") === "Bash,Read" && t.body === "corpo",
    "agents.ts: parser de frontmatter quebrado",
  );
}

const ok = <T,>(r: PromiseSettledResult<T>): T | null => (r.status === "fulfilled" ? r.value : null);
const falha = (r: PromiseSettledResult<unknown>): SourceError | null =>
  r.status === "rejected" ? (r.reason as SourceError) : null;

const erroInline = (e: SourceError | null): string =>
  errorBox(e?.source ?? "origem", e?.message ?? "sem resposta", e?.next ?? "tente de novo");

/** Badge "vN" do Foundation: derivado do conteúdo (campo de versão) ou do sha
 *  que o GET contents já devolve — o GitHub não guarda "v6" como atributo. */
const versaoDe = (texto: string, sha: string): string => {
  const m = texto.match(/\bversion:\s*v?(\d+)/i);
  return m ? `v${m[1]}` : sha.slice(0, 7);
};

// Rol fixo do Claude Code — o contrato diz: não vem de API nenhuma, só o
// estado ON/OFF é dado (frontmatter `tools` do subagent).
const TOOLS: Array<{ id: string; label: string }> = [
  { id: "Bash", label: "Bash" },
  { id: "Read", label: "Read" },
  { id: "Write", label: "Write" },
  { id: "Edit", label: "Edit" },
  { id: "Glob", label: "Glob" },
  { id: "Grep", label: "Grep" },
  { id: "WebFetch", label: "Web fetch" },
  { id: "WebSearch", label: "Web search" },
];

// Forma defensiva do toolset do gateway — o contrato manda GET/PUT
// /v1/mcp/toolset; o nome do campo de servers varia entre versões do LiteLLM.
interface Toolset {
  toolset_name?: string;
  name?: string;
  mcp_servers?: string[];
  mcp_server_ids?: string[];
  servers?: string[];
}

function serversDoToolset(bruto: unknown, agente: Agent): string[] {
  const b = bruto as { toolsets?: Toolset[]; data?: Toolset[] } | Toolset[] | null;
  const arr: Toolset[] = Array.isArray(b) ? b : (b?.toolsets ?? b?.data ?? []);
  const meu = arr.find((t) =>
    [t.toolset_name, t.name].some((n) => n === agente.agent_id || n === agente.agent_name),
  );
  return meu?.mcp_servers ?? meu?.mcp_server_ids ?? meu?.servers ?? [];
}

/** Ids de server que pertencem a algum access group compartilhado do gateway —
 *  é daí que sai o badge "Global". */
function idsGlobais(bruto: unknown): Set<string> {
  const out = new Set<string>();
  const b = bruto as { data?: unknown[] } | unknown[] | null;
  const grupos = Array.isArray(b) ? b : (b?.data ?? []);
  for (const g of grupos as Array<Record<string, unknown>>) {
    for (const v of Object.values(g ?? {})) {
      if (Array.isArray(v)) for (const x of v) if (typeof x === "string") out.add(x);
    }
  }
  return out;
}

// -------------------------------------------------------------- blocos de UI

const toggle = (kind: string, id: string, on: boolean, disabled = false): string =>
  `<label class="agents-toggle"><input type="checkbox" data-kind="${esc(kind)}" data-id="${esc(id)}"
     ${on ? "checked" : ""} ${disabled ? "disabled" : ""}><span class="agents-knob"></span></label>`;

const linha = (titulo: string, sub: string, controle: string, badge = ""): string =>
  `<div class="agents-row">
     <div><div class="agents-row-title">${esc(titulo)}${badge}</div>
     ${sub ? `<div class="agents-row-sub">${esc(sub)}</div>` : ""}</div>
     ${controle}</div>`;

const secao = (titulo: string, direita: string, corpo: string): string =>
  `<section class="agents-sec">
     <div class="agents-sec-head"><h2 class="agents-sec-title">${esc(titulo)}</h2>${direita}</div>
     ${corpo}</section>`;

// ------------------------------------------------------------------ a página

/** Rotas: /agentes (frota) e /agentes/<id> (Edit Agent). */
export const agents = async (el: HTMLElement): Promise<void> => {
  const id = location.hash.split("/")[2];
  if (id) return editAgent(el, decodeURIComponent(id));

  const header = head({ title: "Agents", sub: "A frota registrada no LiteLLM" });

  await load(el, header, async () => {
    const frota = await litellm.agents();
    if (!frota.length) {
      return empty(
        "Nenhum agente na frota",
        "Agentes são registrados no LiteLLM (POST /v1/agents) e aparecem aqui com nome, modelo e card A2A.",
      );
    }
    return table(
      ["Agent", "Description", "Model"],
      frota.map(
        (a) => `<tr>
          <td><a href="#/agentes/${encodeURIComponent(a.agent_id)}">${esc(a.agent_card_params?.name ?? a.agent_name)}</a></td>
          <td>${esc(a.agent_card_params?.description ?? "—")}</td>
          <td>${a.litellm_params?.model ? pill(a.litellm_params.model) : "—"}</td>
        </tr>`,
      ),
    );
  });
};

// --------------------------------------------------------------- Edit Agent

async function editAgent(el: HTMLElement, id: string): Promise<void> {
  const header = `
    <header class="page-head">
      <div class="agents-head-left">
        <a class="btn agents-back" href="#/agentes" aria-label="Voltar para a lista de agents">←</a>
        <h1 class="page-title">Edit Agent</h1>
      </div>
      <div class="page-actions">
        <button class="btn" id="ag-cancel" type="button">Cancel</button>
        <button class="btn btn-primary" id="ag-save" type="button">Save</button>
      </div>
    </header>`;

  // Estado que os handlers precisam depois do render.
  let agente: Agent | null = null;
  let repo = "";
  let caminhoAgente = "";
  let shaAgente: string | undefined;
  let mcpEditavel = false;

  await load(el, header, async () => {
    const carga = await gateway<Agent | { data?: Agent }>(
      `litellm/v1/agents/${encodeURIComponent(id)}`,
    );
    const a = "agent_id" in carga ? carga : carga.data;
    if (!a) return empty("Agente não encontrado", "Ele pode ter sido removido da frota.");
    agente = a;

    repo = await repoAtual();
    caminhoAgente = `agentes/${a.agent_name}.md`;

    const [fundacaoR, arquivoR, skillsR, mcpR, toolsetR, gruposR, reposR, modelosR, frotaR, initR] =
      await Promise.allSettled([
        arquivo(repo, "agentes/foundation.md"),
        arquivo(repo, caminhoAgente),
        litellm.skills(),
        litellm.mcpServers(),
        gateway<unknown>("litellm/v1/mcp/toolset"),
        gateway<unknown>("litellm/v1/mcp/access_groups"),
        github.repos(),
        litellm.models(),
        litellm.agents(),
        github.contents(repo, "agentes/init"),
      ]);

    const arq = ok(arquivoR); // 404 = arquivo ainda não existe; o Save cria
    shaAgente = arq?.sha;
    const { meta, body } = parseAgentFile(arq?.texto ?? "");

    const memoria = meta.memory === "true";
    const toolsMeta = meta.tools !== undefined ? listaDe(meta.tools) : null; // sem chave = herda tudo
    const mestre = toolsMeta === null || toolsMeta.length > 0;
    const toolOn = (t: string): boolean => (toolsMeta === null ? true : toolsMeta.includes(t));
    const skillsOn = listaDe(meta.skills);
    const reposOn = listaDe(meta.repos);
    const collabsOn = listaDe(meta.collaborators);

    // ---- Setup (INFERIDA no spec: name/title/model, LiteLLM)
    const card = a.agent_card_params ?? {};
    const modeloAtual = a.litellm_params?.model ?? "";
    const modelos = ok(modelosR) ?? [];
    const opcoes = [...new Set([modeloAtual, ...modelos.map((m) => m.id)])].filter(Boolean);
    const campoModelo = modelos.length
      ? `<select class="input" id="ag-model">${opcoes
          .map((m) => `<option value="${esc(m)}" ${m === modeloAtual ? "selected" : ""}>${esc(m)}</option>`)
          .join("")}</select>`
      : `<input class="input" id="ag-model" value="${esc(modeloAtual)}" />`;

    const painelSetup = `
      <div class="field"><label class="label" for="ag-name">Name</label>
        <input class="input" id="ag-name" value="${esc(card.name ?? a.agent_name)}" /></div>
      <div class="field"><label class="label" for="ag-title">Title</label>
        <input class="input" id="ag-title" value="${esc(card.description ?? "")}" /></div>
      <div class="field"><label class="label" for="ag-model">Model</label>${campoModelo}
        <span class="hint">Identidade e modelo vivem no LiteLLM; o Save faz o PATCH lá.</span></div>`;

    // ---- Prompt
    const fundacao = ok(fundacaoR);
    const painelPrompt = `
      <details class="card">
        <summary class="agents-found-head">
          <span class="agents-chevron">›</span>
          <strong>AgentOS Foundation</strong>
          ${fundacao ? pill(versaoDe(fundacao.texto, fundacao.sha)) : ""}
          ${pill("Read-only")}
          <span class="agents-found-side">Sits above your instructions</span>
        </summary>
        <div class="agents-found-body">
          <span class="hint">Managed by AgentOS. Prepended to every published agent's system prompt to enforce runtime invariants.</span>
          ${fundacao
            ? `<pre class="agents-pre">${esc(fundacao.texto)}</pre>`
            : `<span class="hint">agentes/foundation.md ainda não existe em ${esc(repo)} — crie o arquivo no repo para o prefixo aparecer aqui.</span>`}
        </div>
      </details>
      <div class="field" style="margin-top:14px">
        <label class="label" for="ag-inst">Your agent instructions</label>
        <textarea class="textarea agents-inst" id="ag-inst" rows="16">${esc(body)}</textarea>
        <span class="hint">Sits on top of the AgentOS Foundation above. Define your agent's persona, goals, and domain rules here.</span>
      </div>`;

    // ---- Capabilities
    const skills = ok(skillsR);
    const secSkills = skills
      ? skills.length
        ? `<div class="card">${skills
            .map((s) =>
              linha(
                s.name,
                s.description ?? "",
                toggle("skill", s.id ?? s.name, skillsOn.includes(s.id ?? s.name)),
              ),
            )
            .join("")}</div>`
        : `<span class="hint">Nenhuma skill publicada no LiteLLM.</span>`
      : erroInline(falha(skillsR)); // bloqueado: issue #2 (ANTHROPIC_API_KEY no container)

    const servers = ok(mcpR);
    const ligados = new Set(agente ? serversDoToolset(ok(toolsetR), a) : []);
    mcpEditavel = Boolean(servers) && toolsetR.status === "fulfilled";
    const globais = idsGlobais(ok(gruposR));
    const srvId = (s: { server_id?: string; alias?: string; server_name?: string; url?: string }): string =>
      s.server_id ?? s.alias ?? s.server_name ?? s.url ?? "";
    const nLigados = servers ? servers.filter((s) => ligados.has(srvId(s))).length : 0;
    const secMcp = servers
      ? `<div class="card">${servers
          .map((s) =>
            linha(
              s.alias ?? s.server_name ?? srvId(s),
              s.url ?? "",
              toggle("mcp", srvId(s), ligados.has(srvId(s)), !mcpEditavel),
              globais.has(srvId(s)) ? " " + pill("Global") : "",
            ),
          )
          .join("")}</div>${mcpEditavel ? "" : `<span class="hint">Toolset do gateway indisponível — os toggles ficam travados para não sobrescrever o estado às cegas.</span>`}`
      : erroInline(falha(mcpR));

    const reposConta = ok(reposR);
    const secRepos = reposConta
      ? `<div class="card">${reposConta
          .map((r) => linha(r.name, r.full_name, toggle("repo", r.full_name, reposOn.includes(r.full_name))))
          .join("")}</div>
         <div class="card agents-hint">Want this agent to push code, create branches, or open PRs? <a href="#/conexoes">Set up the GitHub MCP in Connections</a></div>`
      : erroInline(falha(reposR));

    const scripts = (ok(initR) ?? []).filter((e) => e.type === "file");
    const secInit = scripts.length
      ? `<div class="card">${scripts
          .map(
            (e) => `<div class="agents-row"><div class="agents-row-title mono">${esc(e.name)}</div>
              <a class="btn" href="${esc(e.html_url)}" target="_blank" rel="noopener">Abrir</a></div>`,
          )
          .join("")}</div>`
      : `<span class="hint">Nenhum script. "+ New" cria um arquivo em agentes/init/ no repo.</span>`;

    const painelCapabilities =
      secao("Memory", "", `<div class="card">${linha("Enable agent memory", "Store and recall learnings across sessions", toggle("memory", "memory", memoria))}</div>`) +
      secao(
        "Tools", "",
        `<div class="card">${linha("Enable agent tools", "", toggle("tools-master", "master", mestre))}</div>
         <div class="card" style="margin-top:10px">${TOOLS.map((t) =>
           linha(t.label, "", toggle("tool", t.id, mestre && toolOn(t.id), !mestre)),
         ).join("")}</div>`,
      ) +
      secao("Skills", "", secSkills) +
      secao("MCP Connections", `<span class="agents-count mono" id="ag-mcp-n">${nLigados}</span>`, secMcp) +
      secao("Repositories", "", secRepos) +
      secao("Init Scripts", `<button class="btn" id="ag-init-new" type="button">+ New</button>`, secInit);

    // ---- Collaborators (INFERIDA no spec: quem este agente pode invocar)
    const frota = ok(frotaR);
    const painelCollab = frota
      ? `<span class="hint">Agentes que este agente pode invocar em subtasks (A2A).</span>
         <div class="card" style="margin-top:10px">${frota
           .filter((x) => x.agent_id !== a.agent_id)
           .map((x) =>
             linha(
               x.agent_card_params?.name ?? x.agent_name,
               x.agent_card_params?.description ?? "",
               toggle("collab", x.agent_id, collabsOn.includes(x.agent_id)),
             ),
           )
           .join("")}</div>`
      : erroInline(falha(frotaR));

    const abas = `<div class="tabs" role="tablist">${["Setup", "Prompt", "Capabilities", "Collaborators"]
      .map((t, i) => `<button class="tab" role="tab" type="button" data-tab="${t}" aria-selected="${i === 0}">${t}</button>`)
      .join("")}</div>`;

    return `<div class="agents-form">${abas}
      <div data-panel="Setup">${painelSetup}</div>
      <div data-panel="Prompt" hidden>${painelPrompt}</div>
      <div data-panel="Capabilities" hidden>${painelCapabilities}</div>
      <div data-panel="Collaborators" hidden>${painelCollab}</div>
    </div>`;
  });

  if (!agente) return; // erro de carga ou agente inexistente: só a mensagem
  const ag = agente as Agent;

  const $ = <T extends Element>(sel: string): T | null => el.querySelector<T>(sel);
  const marcados = (kind: string): string[] =>
    Array.from(el.querySelectorAll<HTMLInputElement>(`input[data-kind="${kind}"]`))
      .filter((i) => i.checked)
      .map((i) => i.dataset.id ?? "");

  // Abas: estado de UI local — trocar não pode perder o que foi editado,
  // então os quatro painéis coexistem e só alternam visibilidade.
  el.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((b) =>
    b.addEventListener("click", () => {
      el.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((x) =>
        x.setAttribute("aria-selected", String(x === b)),
      );
      el.querySelectorAll<HTMLElement>("[data-panel]").forEach(
        (p) => (p.hidden = p.dataset.panel !== b.dataset.tab),
      );
    }),
  );

  // Toggle mestre: OFF trava a lista (comportamento nunca mostrado na
  // evidência; desabilitar preserva o estado sem esconder informação).
  const mestreEl = $<HTMLInputElement>('input[data-kind="tools-master"]');
  mestreEl?.addEventListener("change", () => {
    const itens = el.querySelectorAll<HTMLInputElement>('input[data-kind="tool"]');
    itens.forEach((i) => (i.disabled = !mestreEl.checked));
    if (mestreEl.checked && !marcados("tool").length) itens.forEach((i) => (i.checked = true));
  });

  // Contador de MCP: numerador derivado da contagem ligada (o "/ 20" fica de
  // fora — limite sem dono nativo, issue #8).
  el.querySelectorAll<HTMLInputElement>('input[data-kind="mcp"]').forEach((i) =>
    i.addEventListener("change", () => {
      const c = $<HTMLElement>("#ag-mcp-n");
      if (c) c.textContent = String(marcados("mcp").length);
    }),
  );

  // Cancel descarta o estado local: recarrega das origens. Sem I/O de escrita.
  $<HTMLButtonElement>("#ag-cancel")?.addEventListener("click", () => editAgent(el, id));

  // "+ New" em Init Scripts: PUT de arquivo novo em agentes/init/.
  $<HTMLButtonElement>("#ag-init-new")?.addEventListener("click", async () => {
    const nome = prompt("Nome do script (ex.: setup.sh)");
    if (!nome) return;
    const b = $<HTMLButtonElement>("#ag-init-new")!;
    b.disabled = true;
    try {
      await gateway(`github/repos/${repo}/contents/agentes/init/${nome}`, {
        method: "PUT",
        body: JSON.stringify({
          message: `init script ${nome}`,
          content: paraB64("#!/usr/bin/env bash\n"),
        }),
      });
      toast(`Script agentes/init/${nome} criado`);
      editAgent(el, id);
    } catch (e) {
      toast((e as SourceError).message);
      b.disabled = false;
    }
  });

  // Save: contents primeiro (tem sha, é idempotente), depois o PATCH no
  // LiteLLM, depois o toolset — duas origens, sem transação, ordem do contrato.
  $<HTMLButtonElement>("#ag-save")?.addEventListener("click", async () => {
    const btn = $<HTMLButtonElement>("#ag-save")!;
    btn.disabled = true;
    try {
      const mestreOn = mestreEl?.checked ?? true;
      const fm = [
        `memory: ${$<HTMLInputElement>('input[data-kind="memory"]')?.checked ?? false}`,
        `tools: [${(mestreOn ? marcados("tool") : []).join(", ")}]`,
        `skills: [${marcados("skill").join(", ")}]`,
        `repos: [${marcados("repo").join(", ")}]`,
        `collaborators: [${marcados("collab").join(", ")}]`,
      ].join("\n");
      const texto = `---\n${fm}\n---\n${$<HTMLTextAreaElement>("#ag-inst")?.value ?? ""}`;

      await gateway(`github/repos/${repo}/contents/${caminhoAgente}`, {
        method: "PUT",
        body: JSON.stringify({
          message: `agente ${ag.agent_name}: edição pela UI`,
          content: paraB64(texto),
          ...(shaAgente ? { sha: shaAgente } : {}),
        }),
      });

      await gateway(`litellm/v1/agents/${encodeURIComponent(ag.agent_id)}`, {
        method: "PATCH",
        body: JSON.stringify({
          agent_card_params: {
            name: $<HTMLInputElement>("#ag-name")?.value ?? "",
            description: $<HTMLInputElement>("#ag-title")?.value ?? "",
          },
          litellm_params: { model: $<HTMLInputElement>("#ag-model")?.value ?? "" },
        }),
      });

      if (mcpEditavel) {
        await gateway("litellm/v1/mcp/toolset", {
          method: "PUT",
          body: JSON.stringify({ toolset_name: ag.agent_id, mcp_servers: marcados("mcp") }),
        });
      }

      toast("Alterações salvas");
      editAgent(el, id); // recarrega: o sha do arquivo mudou
    } catch (e) {
      toast((e as SourceError).message);
      btn.disabled = false;
    }
  });
}
